import { readFile } from 'node:fs/promises';
import { Command } from 'commander';
import type { VaultItemType, VaultSensitivity } from '../../types/vault.types.js';
import { parseFields, redactVaultItem, unlockVault } from './shared.js';

interface UpdateOptions {
    type?: VaultItemType;
    title?: string;
    field?: string[];
    body?: string;
    file?: string;
    jsonFile?: string;
    sensitivity?: VaultSensitivity;
    secret?: boolean;
    tag?: string[];
    keyword?: string[];
    passphrase?: string;
    recoveryKey?: string;
    json?: boolean;
}

const ITEM_TYPES: VaultItemType[] = ['server', 'app', 'context', 'runbook', 'prompt', 'instruction', 'note', 'secret'];

export function createVaultUpdateCommand(): Command {
    return new Command('update')
        .description('Update an encrypted vault item')
        .argument('<id>', 'Vault item id')
        .option('--type <type>', `Item type: ${ITEM_TYPES.join(', ')}`)
        .option('--title <value>', 'Item title')
        .option('--field <key=value>', 'Structured field; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--body <text>', 'Text body')
        .option('--file <path>', 'Read body from file')
        .option('--json-file <path>', 'Read structured JSON body from file')
        .option('--sensitivity <level>', 'public | internal | secret')
        .option('--secret', 'Mark item as containing secrets')
        .option('--no-secret', 'Mark item as safe to show without reveal reason')
        .option('--tag <tag>', 'Replace tags; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--keyword <keyword>', 'Replace keywords; repeatable', (value, previous: string[] = []) => [...previous, value], [])
        .option('--passphrase <value>', 'Vault passphrase')
        .option('--recovery-key <value>', 'Recovery key')
        .option('-j, --json', 'Output JSON')
        .action(async function (this: Command, id: string, options: UpdateOptions) {
            if (options.type && !ITEM_TYPES.includes(options.type)) {
                throw new Error(`Unsupported item type "${options.type}". Use one of: ${ITEM_TYPES.join(', ')}`);
            }

            const { store, vmk } = await unlockVault(options);
            const patch: Parameters<typeof store.updatePlainItem>[2] = {};
            if (options.type) patch.type = options.type;
            if (options.title) patch.title = options.title;
            if (options.sensitivity) patch.sensitivity = options.sensitivity;
            if (typeof options.secret === 'boolean') patch.containsSecret = options.secret;
            if (options.tag && options.tag.length > 0) patch.tags = options.tag;
            if (options.keyword && options.keyword.length > 0) patch.keywords = options.keyword;

            if (options.jsonFile) {
                patch.body = JSON.parse(await readFile(options.jsonFile, 'utf-8')) as Record<string, unknown>;
            } else if (options.file) {
                patch.body = await readFile(options.file, 'utf-8');
            } else if (options.body) {
                patch.body = options.body;
            } else if (options.field && options.field.length > 0) {
                patch.body = parseFields(options.field);
            }

            const item = await store.updatePlainItem(vmk, id, patch);
            const useJson = !!(options.json || this.optsWithGlobals().json);
            if (useJson) {
                console.log(JSON.stringify({ item: redactVaultItem(item) }, null, 2));
                return;
            }

            console.log(`Updated vault item: ${item.id}`);
            console.log(`Contains secret: ${item.containsSecret ? 'yes' : 'no'}`);
            console.log(`Tags: ${item.tags.join(', ') || 'none'}`);
        });
}
